import { ArrayFilterFn, DictionaryFilterFn, dictionaryToArray } from './internal'

/**
 * Simple array find (wraps native Array#find)
 */
export function _find<T, R extends T>(collection: T[], predicateFn: ArrayFilterFn<T, R>): R | undefined;

/**
 * Object pick find - pick the given key from each object in the given array and find based on that being === true
 */
export function _find<T>(collection: T[], keyMatcher: keyof T): T | undefined;

/**
 * Object matcher find - find the first object in the given array where each of the corresponding properties is
 * strictly equal (===) to the supplied matcher.
 */
export function _find<T>(collection: T[], objectMatcher: Partial<T>): T | undefined;

/**
 *  Simple object find (takes each Key-Value from the given object and finds based on the value)
 */
export function _find<T extends object, R extends T[keyof T]>(collection: T, predicateFn: DictionaryFilterFn<T, R>): R | undefined;

/**
 * Object pick find for lookup objects - pick the given key from each object and find based on that being === true
 * (Objects that are found at each key of the supplied 'collection' object.)
 */
export function _find<T extends object>(collection: T, keyMatcher: keyof T[keyof T]): T[keyof T] | undefined;

/**
 * Object matcher find - find the first object in the given dictionary where each of the corresponding properties is
 * strictly equal (===) to the supplied matcher.
 */
export function _find<T extends object>(collection: T, objectMatcher: Partial<T[keyof T]>): T[keyof T] | undefined;

/**
 * Null | undefined collection will return undefined.
 */
export function _find<T>(collection: null | undefined, objectMatcher: any): undefined;

/**
 * Override implementation.
 */
export function _find<T>(collection: any, predicate: any): T | undefined {
	if (!collection || !predicate || typeof collection !== 'object') return

	if (typeof predicate === 'string') {
		predicate = { [predicate]: true }
	}

	if (Array.isArray(collection)) {
		if (typeof predicate === 'function') {
			return collection.find(predicate)
		}
		return iterativeFind(collection, predicate)
	}

	const list = dictionaryToArray(collection)

	if (typeof predicate === 'function') {
		// Performance hit here for wrapping the predicate fn in another anonymous function - we need to pass key.
		const found = list.find((item) => predicate(item.value, item.key, collection))
		return found && found.value
	}

	// Performance hit here for extra Array#map - don't expect callers to iterate over dictionaries with 10k+ entries.
	return iterativeFind(list.map(item => item.value), predicate)
}

function iterativeFind(collection: any[], predicateObject: any): any {
	const predicateKeys = Object.keys(predicateObject)
	for (let ii = 0; ii < collection.length; ii++) {
		const item = collection[ii]
		let match = true
		for (const key of predicateKeys) {
			if (item[key] !== predicateObject[key]) {
				match = false
				break
			}
		}
		if (match) return item
	}
}